import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localAnalytics } from '../utils/analytics';
import { supabase } from '../api/initSupabase';
import { isIOS } from '../utils/platform';
import { CloseButton, FontText } from './common';

interface ExitBrowserPromptProps {
  onClose: () => void;
}

export const ExitBrowserPrompt: React.FC<ExitBrowserPromptProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const [originalUrl, setOriginalUrl] = useState<string>('');

  useEffect(() => {
    const updateUrlWithTokens = async () => {
      try {
        setOriginalUrl(window.location.href);

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.access_token && session?.refresh_token) {
          const baseUrl = window.location.href.split('#')[0];
          const newHash = `#access_token=${session.access_token}&refresh_token=${session.refresh_token}&token_type=bearer&expires_in=3600&type=magiclink`;
          const newUrl = baseUrl + newHash;

          window.history.replaceState(null, '', newUrl);
        }
      } catch (error) {
        console.error('Error updating URL with tokens:', error);
      }
    };

    updateUrlWithTokens();

    return () => {
      if (originalUrl) {
        window.history.replaceState(null, '', originalUrl);
      }
    };
  }, []);

  const handleCloseClick = () => {
    localAnalytics().logEvent('ExitBrowserPromptClosed', {
      screen: 'ExitBrowserPrompt',
      action: 'closed',
    });
    onClose();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <CloseButton
          onClose={handleCloseClick}
          analyticsEvent="ExitBrowserPromptClosed"
          themeMode={'light'}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
          }}
        />

        <div style={styles.content}>
          <div style={styles.header}>
            <img
              src="/img/books/paywall_subscription.png"
              alt={t('continue_reading')}
              style={styles.icon}
            />
            <div style={styles.title}>
              <FontText h2 style={{ color: '#1a052f' }}>
                {t('continue_reading_book_browser')}
              </FontText>
            </div>
          </div>

          <div style={styles.instructionsContainer}>
            <div style={styles.instruction}>
              <img
                src={
                  isIOS() ? '/img/books/share.png' : '/img/books/three_dots.png'
                }
                alt={isIOS() ? t('share') : t('menu')}
                style={styles.instructionIcon}
              />
              <div style={styles.stepText}>
                <FontText normal style={{ color: '#1a052f' }}>
                  {isIOS()
                    ? t('click_share_instruction_ios')
                    : t('click_three_dots_instruction_android')}
                </FontText>
              </div>
            </div>
            <div style={styles.instruction}>
              <img
                src={
                  isIOS()
                    ? '/img/books/safari.png'
                    : '/img/books/open_external.png'
                }
                alt={isIOS() ? t('open_safari') : t('open_chrome')}
                style={styles.browserIcon}
              />
              <div style={styles.stepText}>
                <FontText normal style={{ color: '#1a052f' }}>
                  {isIOS()
                    ? t('click_open_safari_ios')
                    : t('click_open_chrome_android')}
                </FontText>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#1a052f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
  },
  modal: {
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: 24,
    width: '100%',
    maxWidth: 335,
    minWidth: 280,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 26,
    padding: 24,
    boxSizing: 'border-box',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 24,
    height: 24,
    cursor: 'pointer',
    zIndex: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    width: '100%',
  },
  icon: {
    width: 'min(237px, 70vw)',
    height: 'auto',
    objectFit: 'contain',
    maxWidth: '100%',
  },
  title: {
    margin: 0,
    textAlign: 'center',
    letterSpacing: '-0.56px',
    lineHeight: '1.2',
    width: '100%',
  },
  instructionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
  },
  instruction: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  instructionIcon: {
    width: 48,
    height: 48,
    objectFit: 'contain',
  },
  browserIcon: {
    width: 48,
    height: 48,
    objectFit: 'contain',
  },
  stepText: {
    margin: 0,
    lineHeight: '1.2',
    flex: 1,
  },
};
